import { CONFIG } from '../config';
import { usedToday, usedTonight, useTonight } from '../state/daily';
import type { CharacterLook } from '../state/types';
import { barSpot } from '../world/barSpots';
import { SPOTS, type Vec2 } from '../world/layout';
import { COOTER_SLOT, roomSpot } from '../world/rooms';
import { byBody } from './looks';
import { CLERK_SPOTS, TOWNIES } from './townies';
import { all, needItem, needMoney, onceToday, type Choice, type GameContext, type Scene } from './types';

export interface NpcDef {
  id: string;
  name: string;
  title: string;
  look: CharacterLook;
  voice: number;
  home: Vec2;
  wander: number;
  /** Active hours [start, end). End may exceed 24 for after-midnight (e.g. 26 = 2 AM). */
  hours: [number, number];
  sitting?: boolean;
  facing?: number;
  lines: (ctx: GameContext) => string[];
  choices: (ctx: GameContext) => Choice[];
}

const look = (l: Partial<CharacterLook> & Pick<CharacterLook, 'skin' | 'hair'>): CharacterLook => ({
  hairColor: '#5b3a1e',
  beard: 'none',
  top: 'tee',
  topColor: '#dddddd',
  bottom: 'jeans',
  bottomColor: '#3b5b8c',
  shoes: 'sneakers',
  shoeColor: '#eeeeee',
  hat: 'none',
  hatColor: '#333333',
  belly: 0.3,
  height: 1,
  ...l,
});

/** "Crack a cold one with <name>" — the universal currency of friendship. */
function shareBeer(id: string, name: string, toasts: string[]): Choice {
  return {
    label: `Crack a cold one with ${name}`,
    hint: '-1 :beer:',
    requires: all(needItem('beer', 1, 'beer'), onceToday(`beer_${id}`, 'Already had one together today')),
    run: ({ store, rng, sfx }) => {
      store.take('beer');
      store.incr('beers');
      store.markToday(`beer_${id}`);
      sfx('canOpen');
      return { text: rng.pick(toasts), clout: 3, stats: { buzz: 15 }, items: { cans: 1 } };
    },
  };
}

const MYSTERY_BAG: ReadonlyArray<{
  text: string;
  give?: Parameters<GameContext['store']['give']>[0];
  n?: number;
  money?: number;
}> = [
  { text: 'Two pounds of copper wire. Terry: "Don\'t ask."', give: 'copper', n: 2 },
  { text: 'A car part. Fits a Camaro, he swears on his mama.', give: 'parts', n: 1 },
  { text: 'A scratch-off ticket. Already scratched? No! Probably not.', give: 'scratcher', n: 1 },
  { text: 'Fireworks with all the labels in Chinese. Even the "fuse" part.', give: 'fireworks', n: 1 },
  { text: 'A sack of Sweet Feed. For your "horse."', give: 'mash', n: 1 },
  { text: 'A single left Croc. Terry insists the right one is "coming."' },
  {
    text: 'A rubber band around $35 in mixed bills. Terry looks genuinely upset he sold you this.',
    money: 35,
  },
];

const PARK_NPCS: readonly NpcDef[] = [
  {
    id: 'darlene',
    name: 'Darlene',
    title: 'Park Manager',
    voice: 330,
    home: { x: -8.4, z: 42.4 },
    wander: 3.5,
    hours: [8, 21],
    look: look({
      body: 'woman',
      skin: '#f0c3a0',
      hair: 'teased',
      hairColor: '#f2d16b',
      top: 'tracksuit',
      topColor: '#d94f9a',
      bottom: 'sweats',
      bottomColor: '#d94f9a',
      shoes: 'slippers',
      shoeColor: '#f7e3ef',
      belly: 0.45,
      height: 0.94,
      shades: true,
    }),
    lines: ({ store }) => {
      const r = store.rentStatus();
      const lines = [
        "Rent's due Friday, sugar. I ain't runnin' a charity. I'm runnin' a trailer park, which is worse.",
        "Peanut bit the mailman again. Good boy. Mailman's fine. Mostly.",
        'If you see Dale, tell him I know about the extension cord.',
        "No fireworks in the park. That's the rule. Nobody follows it, but it's the rule.",
      ];
      if (store.flag('apex')) {
        lines.push(
          "New owners sent me a 'mission statement.' I used it to level the coffee table.",
          "Apex says rent goes up every week now. I asked 'to what end?' They said 'shareholder value.' I said 'bless your heart.'",
        );
      }
      if (store.state.rent.strikes > 0) {
        lines.push(
          `You're on strike ${store.state.rent.strikes}, hon. Don't make me tape somethin' to your door.`,
        );
      }
      if (r.paid) lines.push("You're paid up this week. Look at you, bein' a responsible adult.");
      return lines;
    },
    choices: ({ store }) => {
      const r = store.rentStatus();
      const choices: Choice[] = [];
      if (!r.paid) {
        choices.push({
          label: `Pay this week's lot rent`,
          hint: `-$${r.amount}`,
          requires: needMoney(r.amount),
          run: ({ store, sfx }) => {
            store.payRent();
            sfx('cash');
            return "Darlene counts it twice, licks her thumb, counts it again. 'Pleasure doin' business, sugar.'";
          },
        });
      }
      choices.push({
        label: 'Compliment Peanut',
        hint: '+Clout',
        requires: onceToday('peanut'),
        run: ({ store }) => {
          store.markToday('peanut');
          return { text: 'Peanut growls at you. Lovingly, Darlene insists.', clout: 2 };
        },
      });
      if (store.flag('apex')) {
        choices.push({
          label: 'Ask about the new owners',
          run: () =>
            "'Apex Capital Holdings. Some fella in a vest from two states over. Never set foot here. Bought six parks this year.' She lights a long skinny cigarette. 'They call it a portfolio. I call it Tuesday.'",
        });
      }
      choices.push({
        label: 'Ask about work',
        run: () =>
          "'Board at Suds-N-Duds has odd jobs. Junkyard Jerry pays for cans and scrap. And for God's sake, fix that Camaro. It's been on blocks so long it's got squatters' rights.'",
      });
      return choices;
    },
  },
  {
    id: 'earl',
    name: 'Big Earl',
    title: 'Grill Sergeant',
    voice: 140,
    home: SPOTS.earlGrill,
    wander: 3,
    hours: [9, 24],
    look: look({
      skin: '#e0a07a',
      hair: 'mullet',
      hairColor: '#8a5a30',
      beard: 'full',
      top: 'camo',
      topColor: '#5e6b3a',
      bottom: 'jorts',
      bottomColor: '#5b7fae',
      shoes: 'boots',
      shoeColor: '#5a3a1e',
      hat: 'trucker',
      hatColor: '#2b6cb0',
      belly: 0.95,
      height: 1.07,
    }),
    lines: ({ store }) => [
      'Git-R-Done.',
      `NASCAR's just turnin' left, ${byBody(store.state.player.look, 'son', "darlin'")}. And so is life, if you think about it.`,
      'This grill has outlived two marriages and a house fire.',
      "Tank's a sweetheart. Don't let the sixty pounds of muscle fool ya.",
      "Scrap pays better'n cans. Jerry'll take anything shiny. Even you, probably.",
    ],
    choices: () => [
      shareBeer('earl', 'Earl', [
        "Earl clinks your can. 'To turnin' left.'",
        'Earl crushes his can on his forehead. You try it. It hurts. Earl nods with respect.',
        "'This is the good stuff,' Earl says, about the worst beer in America.",
      ]),
      {
        label: 'Bum a hot dog',
        hint: '+1 :hotdog:',
        requires: onceToday('earl_dog', "Earl says one's the limit"),
        run: ({ store }) => {
          store.markToday('earl_dog');
          return {
            text: "Earl hands you a dog straight off the grill. 'Don't tell Darlene I'm feedin' strays.'",
            items: { hotdog: 1 },
          };
        },
      },
      {
        label: 'Sell Earl your catfish (fish fry)',
        hint: `+$${CONFIG.catfishPrice} each`,
        requires: needItem('catfish', 1, 'catfish'),
        run: ({ store, sfx }) => {
          const n = store.count('catfish');
          store.give('catfish', -n);
          store.incr('catfishSold', n);
          sfx('cash');
          return {
            text: `Earl inspects ${n === 1 ? 'it' : `all ${n}`} like a jeweler. 'Now that's a fish fry.' He peels off cash from a roll he keeps in his sock.`,
            money: n * CONFIG.catfishPrice,
            clout: 1,
          };
        },
      },
      {
        label: 'Ask about the Camaro',
        run: ({ store }) =>
          store.state.camaro >= 100
            ? "'She runs! I'll be damned. Take her for a rip, but stay off my lawn. And Jerry'd give ya twelve hunnerd for her.'"
            : `'Needs about ${Math.ceil(((100 - store.state.camaro) / 100) * CONFIG.camaroPartsNeeded)} more parts and a prayer. Jerry sells the parts. Prayer's free.'`,
      },
      {
        label: 'Talk NASCAR',
        hint: '20 min',
        run: () => ({
          text: "You talk NASCAR for twenty minutes. You still don't understand the rules. Neither does Earl. It's beautiful.",
          clout: 1,
          minutes: 20,
        }),
      },
    ],
  },
  {
    id: 'tammy',
    name: 'Tammy Lynn',
    title: 'Independent Wellness Ambassador™',
    voice: 420,
    home: { x: -13.8, z: 30.2 },
    wander: 3.5,
    hours: [9, 22],
    look: look({
      body: 'woman',
      skin: '#f3c4a6',
      hair: 'teased',
      hairColor: '#4a2a18',
      top: 'leopard',
      topColor: '#d9a441',
      bottom: 'jeans',
      bottomColor: '#2d3f73',
      shoes: 'sneakers',
      shoeColor: '#ffffff',
      belly: 0.2,
      height: 0.93,
    }),
    lines: () => [
      'Hey hun!! :hearts: Have you ever thought about being your OWN boss??',
      'Some people in this park need to MIND. THEIR. BUSINESS. Not you. Unless...',
      "These oils cured my cousin's warts AND her divorce.",
      'Live, laugh, love, hun. But mostly live. On a budget.',
      "Did you see what Crystal posted?? Don't look. Okay, look.",
    ],
    choices: ({ store }) => [
      store.flag('mlm_joined')
        ? {
            label: 'Ask about your "commissions"',
            run: () =>
              "'Your downline is gonna EXPLODE, hun. You just gotta invest in a little more inventory.' You already have a closet full of oils.",
          }
        : {
            label: 'Join her downline',
            hint: '-$50',
            requires: needMoney(50),
            run: ({ store, sfx }) => {
              store.spend(50);
              store.incr('mlm');
              store.setFlag('mlm_joined', store.day);
              sfx('cash');
              return "Tammy SQUEALS. You're now an Independent Wellness Ambassador™. Starter kit: six oils, a vision board, and a creeping sense of dread.";
            },
          },
      {
        label: 'Ask for the tea',
        requires: onceToday('tea'),
        run: ({ store, rng }) => {
          store.markToday('tea');
          return {
            text: `Tammy leans in. "${rng.pick([
              'Dale owes money to three people and one of them is a goat breeder.',
              "Darlene's been seein' the propane guy. Every Thursday. Propane don't take two hours.",
              "Old Man Jenkins was a sniper in 'Nam. Or a cook. Depends on the day.",
              "Skeeter sells fireworks out of his trunk. Cheaper than the gas station. You didn't hear it from me.",
              "If you hang out on a trampoline with a little buzz, you get braver. That's science. I read it.",
              'Junkyard Jerry buys Camaros. Running ones. For a LOT.',
            ])}"`,
            clout: 1,
          };
        },
      },
      {
        label: 'Like her latest post',
        hint: '+Clout',
        requires: onceToday('like_tammy'),
        run: ({ store }) => {
          store.markToday('like_tammy');
          return {
            text: "'OMG you're SO sweet :heart-eyes:' She immediately posts about how sweet you are.",
            clout: 2,
          };
        },
      },
    ],
  },
  {
    id: 'dale',
    name: 'Cousin Dale',
    title: 'Entrepreneur (Self-Described)',
    voice: 210,
    home: { x: 13.2, z: 17.5 },
    wander: 9,
    hours: [10, 27],
    look: look({
      skin: '#efae86',
      hair: 'rattail',
      hairColor: '#a0682e',
      beard: 'goatee',
      top: 'shirtless',
      bottom: 'jorts',
      bottomColor: '#4d6f9f',
      shoes: 'crocs',
      shoeColor: '#ff7b2e',
      hat: 'backwards',
      hatColor: '#222222',
      belly: 0.3,
      height: 1,
    }),
    lines: () => [
      'I got a guy.',
      'Hold my beer. Actually, hold my beer, I gotta run.',
      "Lemme borrow twenty bucks. I'm good for it. Probably.",
      "The cops are lookin' for a guy who looks EXACTLY like me. Crazy coincidence.",
      "I'm between jobs. The jobs are very far apart.",
    ],
    choices: ({ store }) => {
      const owed = Number(store.flag('dale_owed') ?? 0);
      const choices: Choice[] = [
        {
          label: 'Lend him $20',
          hint: '-$20',
          requires: all(needMoney(20), onceToday('lend_dale', 'Even you have limits')),
          run: ({ store }) => {
            store.spend(20);
            store.markToday('lend_dale');
            store.setFlag('dale_owed', owed + 20);
            return {
              text: "Dale hugs you for too long. 'You won't regret this.' You already do.",
              clout: 1,
            };
          },
        },
        {
          label: 'Hold his beer',
          requires: onceToday('hold_dale'),
          run: ({ store, rng, sfx }) => {
            store.markToday('hold_dale');
            const success = rng.chance(0.55);
            sfx(success ? 'airhorn' : 'fail');
            return success
              ? {
                  text: 'Dale backflips off the roof of his truck and STICKS THE LANDING. The whole park loses its mind.',
                  clout: 3,
                  post: {
                    author: 'Dale "The Deal" Dupree',
                    avatar: 'dale',
                    text: 'TOLD YALL :fire::fire::fire: (video)',
                    likes: 61,
                  },
                }
              : {
                  text: "Dale tries to jump the ditch on a kid's bike. He does not jump the ditch. He is fine. The bike is not.",
                  clout: 2,
                  post: {
                    author: 'Dale "The Deal" Dupree',
                    avatar: 'dale',
                    text: 'that ditch is rigged',
                    likes: 12,
                  },
                };
          },
        },
        {
          label: 'Ask about his "business"',
          run: (): Scene => ({
            title: "Dale's Business Opportunity",
            speaker: { id: 'dale', name: 'Cousin Dale', voice: 210 },
            text: "'Artisanal firewood. I get it from the state park — don't worry about it — and sell it to campers at the state park. Thirty bucks gets you in on the ground floor.'",
            choices: [
              {
                label: 'Invest $30',
                hint: '-$30',
                requires: all(
                  needMoney(30),
                  ({ store }) => !store.flag('dale_invest_day') || 'Already invested',
                ),
                run: ({ store, rng }) => {
                  store.spend(30);
                  store.setFlag('dale_invest_day', store.day + 1);
                  store.setFlag('dale_invest_win', rng.chance(0.45));
                  return "Dale shakes your hand with both of his hands. That's a bad sign. Check back tomorrow.";
                },
              },
            ],
            leave: 'Pass',
          }),
        },
      ];
      if (owed > 0) {
        choices.push({
          label: `Ask for your $${owed} back`,
          requires: onceToday('dun_dale', 'He already dodged you today'),
          run: ({ store, rng }) => {
            store.markToday('dun_dale');
            if (rng.chance(0.35)) {
              store.setFlag('dale_owed', 0);
              return {
                text: "Dale pays you back IN FULL, plus ten 'for your trouble,' entirely in quarters and one arcade token.",
                money: owed + 10,
              };
            }
            return rng.pick([
              'Dale suddenly hears his mama calling. His mama lives in Tennessee.',
              "'Money's tied up in investments.' The investments are scratch-offs.",
              "'Tomorrow. Swear on my truck.' His truck was repossessed in March.",
            ]);
          },
        });
      }
      return choices;
    },
  },
  {
    id: 'jenkins',
    name: 'Old Man Jenkins',
    title: 'Resident Since 1974',
    voice: 120,
    home: SPOTS.jenkinsChair,
    wander: 0,
    hours: [7, 21],
    sitting: true,
    facing: 0,
    look: look({
      skin: '#e8bfa3',
      hair: 'bald',
      hairColor: '#e8e8e8',
      beard: 'full',
      top: 'tee',
      topColor: '#d8d0b8',
      bottom: 'overalls',
      bottomColor: '#3f5f8f',
      shoes: 'boots',
      shoeColor: '#3b2a1a',
      belly: 0.3,
      height: 0.93,
    }),
    lines: () => [
      "Birds ain't real. Watched one charge itself on a power line.",
      'Back in my day, a trailer cost four thousand dollars and a handshake.',
      'Get off my lawn. ...Not you. The concept of youth.',
      "Moon landing was real. It's Ohio that's fake.",
      "Been here since '74. Seen four tornadoes, three marriages and one good haircut.",
    ],
    choices: () => [
      {
        label: 'Listen to a story',
        hint: '30 min',
        requires: onceToday('jenkins_story', "He's napping with his eyes open"),
        run: ({ store, rng }) => {
          store.markToday('jenkins_story');
          return {
            text: rng.pick([
              "Jenkins tells you about the summer of '78, when a tornado moved his trailer six feet to the left and 'improved the view.'",
              'Jenkins tells you how he met his late wife: she hit him with her car. He says it was love at first impact.',
              "Jenkins tells you about the time he arm-wrestled a county commissioner for the park's water rights. He lost. Then he won the rematch.",
            ]),
            clout: 2,
            minutes: 30,
          };
        },
      },
      {
        label: 'Ask about the war',
        run: () => ({ text: "'Which one?' he says, and does not elaborate.", clout: 1 }),
      },
      {
        label: 'Ask if he needs his lawn mowed',
        run: () =>
          "'Check the board at the laundromat. I don't hire direct. Taxes.' He winks. You don't know what it means.",
      },
    ],
  },
  {
    id: 'skeeter',
    name: 'Skeeter',
    title: 'Future YouTuber',
    voice: 260,
    home: SPOTS.skeeterRamp,
    wander: 5,
    hours: [11, 25],
    look: look({
      skin: '#f1c6a8',
      hair: 'buzz',
      hairColor: '#c9a25c',
      top: 'tee',
      topColor: '#1e1e1e',
      bottom: 'jorts',
      bottomColor: '#6a8cc0',
      shoes: 'sneakers',
      shoeColor: '#e33',
      hat: 'backwards',
      hatColor: '#e33',
      belly: 0,
      height: 0.97,
    }),
    lines: () => [
      'Bro.',
      'No cap, I seen a possum the size of a Pontiac.',
      'These fireworks are legal in like two states. Not this one. Probably.',
      "I'm gonna be a YouTuber. I just need a phone that holds a charge.",
      'You got your Crocs in sport mode? Respect.',
    ],
    choices: () => [
      {
        label: 'Buy trunk fireworks',
        hint: '-$10',
        requires: needMoney(10),
        run: ({ store, sfx }) => {
          store.spend(10);
          sfx('cash');
          return {
            text: "Skeeter pops his trunk. 'Don't light it near your face, bro. Or do. Content.'",
            items: { fireworks: 1 },
          };
        },
      },
      {
        label: 'Ask for a stunt tip',
        run: () =>
          "'Trampoline stunts, bro. Time the bounce in the green. And a little buzz makes the green bigger. That's just facts.' (Buy a trampoline at Lucky's Pawn.)",
      },
      {
        label: 'Dap him up',
        hint: '+Clout',
        requires: onceToday('dap'),
        run: ({ store }) => {
          store.markToday('dap');
          return {
            text: "You dap Skeeter up. It's elaborate. It has seven steps. You nail all of them.",
            clout: 1,
          };
        },
      },
    ],
  },
  {
    id: 'jerry',
    name: 'Junkyard Jerry',
    title: 'Proprietor, Scrap & Salvage',
    voice: 160,
    home: SPOTS.junkyardWindow,
    wander: 0,
    hours: [7, 19],
    facing: 0,
    look: look({
      skin: '#d9a07c',
      hair: 'buzz',
      hairColor: '#9a9a9a',
      beard: 'full',
      top: 'flannel',
      topColor: '#b03a2e',
      bottom: 'jeans',
      bottomColor: '#34405a',
      shoes: 'boots',
      shoeColor: '#4a3320',
      hat: 'trucker',
      hatColor: '#e0b000',
      belly: 0.7,
      height: 1.02,
    }),
    lines: () => [
      "Cans, scrap, car parts. I buy it all. Except dignity. Market's flooded.",
      "Fifty cents a can. Don't argue, I didn't invent aluminum.",
      'That Camaro of yours? Twelve hundred if she runs. Zero if she sits.',
    ],
    choices: () => [
      {
        label: 'Do business',
        run: ({ ui }) => {
          ui.openShop('junkyard');
        },
      },
    ],
  },
  {
    id: 'pete',
    name: 'Possum Pete',
    title: 'Back Forty Hermit',
    voice: 175,
    home: SPOTS.pete,
    wander: 3,
    hours: [6, 23],
    look: look({
      skin: '#c9935f',
      hair: 'bald',
      hairColor: '#c8c8c0',
      beard: 'full',
      top: 'flannel',
      topColor: '#7a3b2e',
      bottom: 'overalls',
      bottomColor: '#4a5a7a',
      shoes: 'boots',
      shoeColor: '#4a3020',
      hat: 'cowboy',
      hatColor: '#6b4a2a',
      belly: 0.2,
      height: 0.97,
    }),
    lines: () => [
      "Been livin' in that bus since '03. Rent's free. Plumbing is a bucket.",
      'The moon landing was real. The moon is what I got questions about.',
      "Catfish holes are along the crick bank. Red rags on the stakes. Stick your arm in and let 'em bite. Then pull. Not too hard.",
      "Lot 13? Burned down in '98. Grease fire. Or a ghost. Depends who's askin' and how much they've had.",
      "I trade. Catfish for shine. Shine for catfish. It's a whole economy back here.",
    ],
    choices: ({ store }) => [
      {
        label: 'Trade Pete a catfish for a jar of shine',
        hint: '-1 :catfish: · +1 :moonshine:',
        requires: all(
          needItem('catfish', 1, 'catfish'),
          onceToday('pete_trade', 'Pete only trades once a day'),
        ),
        run: ({ store: st }) => {
          st.markToday('pete_trade');
          st.give('catfish', -1);
          return {
            text: "Pete sniffs the catfish, nods, and hands you a mason jar from a cooler that's been running off a car battery since 2009.",
            items: { shine: 1 },
          };
        },
      },
      {
        label: "Ask about noodlin'",
        run: () =>
          "'Pick a hole. Stick your arm in to the elbow. When it bites, pull steady. Too hard and it rips your arm loose, too soft and it swims off. And if it feels like a rock with a beak, that's a snapper. Pray.'",
      },
      {
        label: "Ask about Uncle Rudy's still",
        run: () =>
          `'Up the holler, west side, past the junk pile. Rudy's doin' a little time, so it's open. Needs a sack of Sweet Feed from the Gas-N-Grub. Keep her between 173 and 208 degrees. Hotter'n that and she blows. Ask me how I know.' He has no eyebrows.`,
      },
      {
        label: 'Ask about Lot 13',
        run: () =>
          store.counter('lot13') > 0
            ? "'You went pokin' around in there? Huh. Did the chair rock? ...The chair always rocks.'"
            : "'Burned down in '98. The Hendricks boy left a pan of bacon on and went to the Waffle... to breakfast. Some folks say you can still smell the bacon at night. I say that's just me cookin'.'",
      },
    ],
  },
  {
    id: 'terry',
    name: 'Trenchcoat Terry',
    title: 'Entrepreneur',
    voice: 240,
    home: SPOTS.terry,
    wander: 1.5,
    hours: [19, 27],
    look: look({
      skin: '#f0c3a0',
      hair: 'rattail',
      hairColor: '#2b1d12',
      beard: 'goatee',
      top: 'tracksuit',
      topColor: '#8a7a5a',
      bottom: 'jeans',
      bottomColor: '#2b2b3a',
      shoes: 'sneakers',
      shoeColor: '#eeeeee',
      hat: 'backwards',
      hatColor: '#1d1d1d',
      belly: 0.2,
      height: 1.02,
      shades: true,
    }),
    lines: () => [
      "Psst. You buyin' or sellin'? Either way, keep your voice down. The barrels have ears.",
      "I got a guy who's got a guy. I AM the guy, actually. Don't tell my guy.",
      'Shine moves good on Friday nights. Something about rent day.',
      'Everything in this coat is one hundred percent legitimate. Except the coat.',
    ],
    choices: ({ store }) => {
      const sold = usedTonight(store, 'terry_shine');
      const room = Math.max(0, 4 - sold);
      const n = Math.min(room, store.count('shine'));
      return [
        {
          label: n > 0 ? `Sell Terry ${n} jar${n > 1 ? 's' : ''} of shine` : 'Sell Terry moonshine',
          hint: `+$${CONFIG.shinePrice} each`,
          requires: () =>
            room <= 0 ? 'Terry only moves 4 jars a night' : store.has('shine') || 'No moonshine to sell',
          run: ({ store: st, sfx }) => {
            st.give('shine', -n);
            st.incr('shineSold', n);
            useTonight(st, 'terry_shine', n);
            sfx('cash');
            return {
              text: `Terry holds a jar up to the barrel fire, shakes it, watches the bead. 'That's the good stuff.' The jars vanish into the coat. So does he, almost.`,
              money: n * CONFIG.shinePrice,
            };
          },
        },
        {
          label: 'Buy a Mystery Bag',
          hint: '-$10',
          requires: all(
            needMoney(10),
            () => usedTonight(store, 'terry_bag') < 1 || 'One per customer per night',
          ),
          run: ({ store: st, rng }) => {
            useTonight(st, 'terry_bag');
            st.spend(10);
            const prize = rng.pick(MYSTERY_BAG);
            if (prize.give) st.give(prize.give, prize.n ?? 1);
            return {
              text: `Terry pulls a paper sack out of the coat. Inside: ${prize.text}`,
              money: prize.money ?? 0,
            };
          },
        },
        {
          label: "Ask what's in the coat",
          run: () =>
            "Terry opens the coat a crack. Watches. Lots of watches. A rotisserie chicken. A smaller coat. He closes it. 'Seen enough.'",
        },
      ];
    },
  },
  {
    id: 'ricky',
    name: "Lil' Ricky",
    title: 'Tri-County Cornhole Champ (Self-Proclaimed)',
    voice: 300,
    home: SPOTS.ricky,
    wander: 2,
    hours: [11, 22],
    look: look({
      skin: '#eab08c',
      hair: 'buzz',
      hairColor: '#c9a25c',
      top: 'tank',
      topColor: '#c62828',
      bottom: 'sweats',
      bottomColor: '#3a3a3a',
      shoes: 'crocs',
      shoeColor: '#ffd400',
      hat: 'backwards',
      hatColor: '#c62828',
      belly: 0.05,
      height: 0.9,
    }),
    lines: () => [
      'Tri-County Cornhole Champion, two years running. Well. One year. It was a small tri-county.',
      "Bags in the hole, money in the pocket. That's the Ricky way.",
      "You wanna play for money or you wanna play for feelings? 'Cause I take both.",
      "My mama says I could go pro. There's no pro. That's how good I am.",
    ],
    choices: ({ store }) => {
      const left = CONFIG.cornholeGamesPerDay - usedToday(store, 'cornhole');
      const bet = (amount: number) => ({
        label: `Play cornhole for $${amount}`,
        hint: `win +$${amount}`,
        requires: all(needMoney(amount), () => left > 0 || "Ricky's got a shift. Tomorrow."),
        run: ({ ui }: GameContext) => {
          ui.playCornhole(amount);
        },
      });
      return [bet(5), bet(10), bet(20)];
    },
  },
  {
    id: 'lurlene',
    name: 'Lurlene',
    title: 'Bartender, the Rusty Spur',
    voice: 280,
    home: barSpot('bartender'),
    wander: 1.2,
    hours: [14, 26],
    look: look({
      body: 'woman',
      skin: '#f1c6a8',
      hair: 'teased',
      hairColor: '#e8c070',
      top: 'leopard',
      topColor: '#d9a441',
      bottom: 'jeans',
      bottomColor: '#1d2b3a',
      shoes: 'boots',
      shoeColor: '#2b1d14',
      belly: 0.15,
      height: 0.98,
    }),
    lines: () => [
      "What'll it be, sugar? And don't say 'surprise me.' I will.",
      'The bull goes up to "divorce." I only set it there for people I like.',
      "I've thrown out three men tonight and it's a Tuesday. Behave.",
      'Tips are appreciated. Compliments are tolerated. Pickup lines are a two-drink fine.',
    ],
    choices: ({ store }) => [
      {
        label: 'Draft beer',
        hint: '-$3',
        requires: needMoney(3),
        run: ({ store: st, sfx }) => {
          st.spend(3);
          st.incr('beers');
          sfx('canOpen');
          return {
            text: "Lurlene slides a sweating draft down the bar. It stops exactly in front of you. She doesn't even look.",
            stats: { buzz: 16, energy: 4 },
            clout: 1,
          };
        },
      },
      {
        label: 'Shot of Fire Bawl',
        hint: '-$4',
        requires: needMoney(4),
        run: ({ store: st }) => {
          st.spend(4);
          return {
            text: 'It burns like a candle that hates you. The whole bar yells "WOOO" without being asked.',
            stats: { buzz: 24, health: -2 },
            clout: 2,
          };
        },
      },
      {
        label: 'Throw darts with her',
        hint: '$10 bet',
        requires: needMoney(10),
        run: ({ ui }) => {
          ui.play('darts', 10);
        },
      },
      {
        label: 'Ride the mechanical bull',
        hint: `-$5`,
        requires: needMoney(5),
        run: ({ ui }) => {
          ui.play('mechbull', 5);
        },
      },
      {
        label: 'Start a tab',
        requires: () =>
          !store.flag('tab_open') || 'Your tab is already open. Lurlene has a separate notebook for you.',
        run: ({ store: st }) => {
          st.setFlag('tab_open', true);
          st.incr('barTabs');
          return "Lurlene writes your name on a napkin and sticks it to the register. \"That's a tab. That's a commitment. Don't make me come find you.\" (She's kidding. She's not.)";
        },
      },
    ],
  },
  {
    id: 'tiny',
    name: 'Big Tiny',
    title: 'Undefeated (Since Tuesday)',
    voice: 120,
    home: barSpot('regular'),
    wander: 0.4,
    hours: [16, 26],
    look: look({
      skin: '#d9a07c',
      hair: 'bald',
      hairColor: '#5b3a1e',
      beard: 'full',
      top: 'flannel',
      topColor: '#7a1f2b',
      bottom: 'overalls',
      bottomColor: '#3b5b8c',
      shoes: 'boots',
      shoeColor: '#3a2416',
      hat: 'trucker',
      hatColor: '#2f7d32',
      belly: 0.7,
      height: 1.18,
    }),
    lines: () => [
      "They call me Tiny on account of I'm not.",
      "Arm wrestle? I been waitin' all night. Well. Since lunch.",
      "My mama arm wrestled. Her mama arm wrestled. We're a arm wrestlin' family.",
      "I'd let you win but I don't know how.",
    ],
    choices: ({ store }) => {
      const left = 3 - usedToday(store, 'armwrestle');
      const bet = (amount: number): Choice => ({
        label: `Arm wrestle for $${amount}`,
        hint: `win +$${amount}`,
        requires: all(needMoney(amount), () => left > 0 || "Tiny's arm needs to rest. He says. Tomorrow."),
        run: ({ ui }) => {
          ui.play('armwrestle', amount);
        },
      });
      return [bet(10), bet(25)];
    },
  },
  {
    id: 'cooter',
    name: 'Cooter',
    title: "Cooter's Used Cars",
    voice: 210,
    // Behind the steel-mesh window in his office trailer.
    home: roomSpot(COOTER_SLOT, CLERK_SPOTS.cooter),
    wander: 0,
    facing: 0,
    hours: [9, 19],
    look: look({
      skin: '#eab08c',
      hair: 'mullet',
      hairColor: '#8a6a3a',
      beard: 'stache',
      top: 'nascar',
      topColor: '#c62828',
      bottom: 'jeans',
      bottomColor: '#3b5b8c',
      shoes: 'boots',
      shoeColor: '#5a3a24',
      hat: 'cowboy',
      hatColor: '#6b4a2b',
      belly: 0.45,
      height: 1.02,
      shades: true,
    }),
    lines: () => [
      'Every car on this lot has been driven by a little old lady. To the bar. Every night.',
      'That donk? Sat on them 26s for nine years. Never once went over a speed bump.',
      "We tote the note! I don't know what that means. My daddy said it and it sold cars.",
      "I don't sell anything economical. Economical is for people with plans.",
    ],
    choices: () => [
      {
        label: 'Look at the cars',
        run: ({ ui }) => {
          ui.openShop('cooter');
        },
      },
    ],
  },
  {
    id: 'randy',
    name: 'Rodeo Randy',
    title: 'Rodeo Clown & Announcer',
    voice: 360,
    home: SPOTS.rodeoRandy,
    wander: 3,
    hours: [16, 23],
    look: look({
      skin: '#f6d6c0',
      hair: 'teased',
      hairColor: '#c62828',
      top: 'nascar',
      topColor: '#ffd400',
      bottom: 'overalls',
      bottomColor: '#c62828',
      shoes: 'sneakers',
      shoeColor: '#2b6cb0',
      hat: 'cowboy',
      hatColor: '#ffffff',
      belly: 0.3,
      height: 0.96,
    }),
    lines: () => [
      "LADIES AND GENTLEMEN... and Dale... it's TIME TO RODEOOOO!",
      'Widowmaker has thrown eleven cowboys this month. Twelve, if you count me. I count me.',
      "The barrel's for hidin' in. I've been hidin' in it since 1994.",
      "Eight seconds is a long time on a bull. It's a short time anywhere else.",
    ],
    choices: () => [
      {
        label: 'Ride Widowmaker',
        hint: '$15 entry · $150 purse',
        requires: needMoney(15),
        run: ({ ui }) => {
          ui.play('rodeo', 15);
        },
      },
      {
        label: 'Ask about the mud bog',
        run: () =>
          "'Pay at the sign, drive through the start arch, get around the far one and haul it back. Fastest times get paid. Lifted trucks and three-wheelers eat that mud for breakfast. A Camaro? A Camaro IS breakfast.'",
      },
    ],
  },
  {
    id: 'gus',
    name: 'Gus',
    title: 'Range Master',
    voice: 150,
    home: SPOTS.gus,
    wander: 2,
    hours: [8, 19],
    look: look({
      skin: '#e0a07a',
      hair: 'buzz',
      hairColor: '#c8c8c0',
      beard: 'full',
      top: 'camo',
      topColor: '#556b2f',
      bottom: 'jeans',
      bottomColor: '#2b2b3a',
      shoes: 'boots',
      shoeColor: '#3a2416',
      hat: 'trucker',
      hatColor: '#c26b1a',
      belly: 0.4,
      height: 1.0,
      shades: true,
    }),
    lines: () => [
      'Eyes and ears. Muzzle down range. Cans only. Those are the rules and I am the rules.',
      "Top shot of the day gets twenty-five bucks. I don't hand it out to just anybody. I hand it out to whoever shoots best.",
      "Breathe out, squeeze, don't yank. That's also my marriage advice.",
      "I've been running this range thirty years. Never lost a can I didn't mean to.",
    ],
    choices: () => [
      {
        label: 'Rent a lane and a rifle',
        hint: '-$8',
        requires: needMoney(8),
        run: ({ ui }) => {
          ui.play('shoot', 8);
        },
      },
    ],
  },
  {
    id: 'meemaw',
    name: 'Meemaw Pearl',
    title: 'Possum Trot Lane, Since Forever',
    voice: 420,
    home: SPOTS.meemaw,
    wander: 0,
    sitting: true,
    facing: -Math.PI / 2,
    hours: [7, 21],
    look: look({
      body: 'woman',
      skin: '#f6dcc8',
      hair: 'teased',
      hairColor: '#e8e8e8',
      top: 'flannel',
      topColor: '#c75b6b',
      bottom: 'sweats',
      bottomColor: '#7a9cc0',
      shoes: 'slippers',
      shoeColor: '#f3d9e6',
      belly: 0.3,
      height: 0.9,
    }),
    lines: () => [
      "Come here, baby, let me look at you. You're too skinny. Everybody's too skinny.",
      'I knew your uncle. He owes me four dollars and a casserole dish.',
      "I've outlived three husbands and two landlords. I'll outlive Apex too.",
      "Back in my day we didn't have phones. We had porches. And we knew everybody's business anyway.",
    ],
    choices: ({ store }) => [
      {
        label: 'Sit for some sweet tea',
        requires: onceToday('meemaw_tea', "Meemaw's watching her stories. Come back tomorrow."),
        run: ({ store: st, rng }) => {
          st.markToday('meemaw_tea');
          return {
            text: rng.pick([
              "The tea is so sweet your teeth file a complaint. Meemaw tells you who's sleeping with who on Possum Trot. It's everybody. It's been everybody since 1987.",
              'Meemaw tells you about the tornado of \'74 that took her first trailer and her second husband. "Only one of them I missed."',
              "She shows you photos of grandkids. You count eleven. She says there's fourteen. She's not sure which three are missing.",
            ]),
            stats: { health: 8, energy: 6 },
            minutes: 30,
          };
        },
      },
      {
        label: 'Take her trash to the dumpster',
        hint: '+$5, +Clout',
        requires: onceToday(
          'meemaw_trash',
          'Already did it today. She said you\'re a good boy. Or girl. She said "good."',
        ),
        run: ({ store: st }) => {
          st.markToday('meemaw_trash');
          return {
            text: 'You haul two bags. Meemaw presses a five-dollar bill and a butterscotch into your hand. The butterscotch is from the Carter administration.',
            money: 5,
            clout: 2,
            minutes: 20,
          };
        },
      },
      ...(store.flag('apex')
        ? [
            {
              label: 'Ask about Apex Capital',
              run: () =>
                "'Brent? Came by here in his little vest and called me \"a legacy resident.\" I called him something too. Y'all ought to buy this park out from under him. Pool your money. Get the whole Lane in on it. I got $40 in a coffee can says you can do it.'",
            },
          ]
        : []),
    ],
  },
];

/** Everybody: the park, the strip, the fairgrounds, and the folks behind the counters. */
export const NPCS: readonly NpcDef[] = [...PARK_NPCS, ...TOWNIES];

export function npcDef(id: string): NpcDef | undefined {
  return NPCS.find((n) => n.id === id);
}

/** Is an NPC out and about at this hour? Handles schedules that run past midnight. */
export function npcActive(def: NpcDef, minutes: number): boolean {
  const h = minutes / 60;
  const [start, end] = def.hours;
  return (h >= start && h < end) || (end > 24 && h < end - 24);
}
